import { Button, Popconfirm, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Image as ZhImage
} from "zhihao-ui-react";

import request from "@/api";
import { advertService } from "@/api/services/advertService";
import { consignmentService } from "@/api/services/consignmentService";
import { infoService } from "@/api/services/infoService";
import { prodTypeService } from "@/api/services/prodTypeService";
import { shipRouteService } from "@/api/services/shipRouteService";
import { DEFAULT_VALUE } from "@/config";
import {
    POP_UP_AD_DISPLAY_FREQUENCY,
    POP_UP_AD_DISPLAY_PAGE,
    POP_UP_AD_DISPLAY_TYPE,
    POP_UP_AD_FUNCTION_PAGE,
    POP_UP_AD_STATE,
    POP_UP_ADVERT_TERMINAL,
    POP_UP_ADVERT_TERMINAL_TYPE
} from "@zhihaoscm/meta";
import { useUserStore } from "@/stores/react/authStore";
import { confirmDialog } from "@/utils/confirmDialog";
import { deletePopUpAdvert, updatePopUpAdvertState } from "./PopUpAdvertPage";

interface PopUpAdvertContent {
    picFileId?: number;
    type?: number | string;
    value?: string;
    sort?: number;
    labelName?: string;
    picFileUrl?: string;
}

export interface PopUpAdvertDetail {
    id: number;
    name?: string;
    terminal?: Array<number | string>;
    displayPage?: number | string;
    displayFrequency?: number | string;
    state?: number | string;
    createdTime?: string;
    content?: PopUpAdvertContent[];
}

export interface PopUpAdvertDetailPageProps {
    loadDetail?: (id: number, params?: { isSort?: boolean }) => Promise<PopUpAdvertDetail>;
    loadPicture?: (id: number) => Promise<{ path?: string }>;
    loadLabelName?: (type: number, value: string) => Promise<string | undefined>;
    remove?: (id: number) => Promise<unknown>;
    updateState?: (id: number, state: number) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 弹窗广告详情接口。 */
export async function loadPopUpAdvertDetail(id: number, params?: { isSort?: boolean }): Promise<PopUpAdvertDetail> {
    const response = await request.service.get<PopUpAdvertDetail>(`/dataService/admin/pop-up-advert/${id}`, {
        params
    });
    return response.data;
}

/** 复用 Vue 文件服务，按文件编号获取弹窗广告模块的缩略图路径。 */
export async function loadPopUpAdvertPicture(id: number): Promise<{ path?: string }> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${id}`);
    return response.data;
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function formatTerminals(values?: Array<number | string>): string {
    if (!values?.length) return DEFAULT_VALUE;
    return values.map(value => POP_UP_ADVERT_TERMINAL.getIntroByCode(value) || DEFAULT_VALUE).join(", ");
}

const formatMeta = (getIntroByCode: (code: number | string | undefined) => string | null, value?: number | string) =>
    getIntroByCode(value) || DEFAULT_VALUE;

/** 按旧 Vue 详情页的跳转类型，将内容编号解析为用户可读的业务名称。 */
async function loadPopUpAdvertContentLabel(item: PopUpAdvertContent): Promise<string | undefined> {
    if (!item.value) return undefined;

    switch (Number(item.type)) {
        case POP_UP_AD_DISPLAY_TYPE.PRODUCT.code: {
            const product = await consignmentService.getDetail(item.value);
            const productConsignment = product?.productConsignment;
            if (!productConsignment) return undefined;
            return `${productConsignment.title || DEFAULT_VALUE} | ${productConsignment.productTypeName || DEFAULT_VALUE}(${productConsignment.productTypeSpec || DEFAULT_VALUE}) | ${productConsignment.id ?? DEFAULT_VALUE}`;
        }
        case POP_UP_AD_DISPLAY_TYPE.SAND_PRICE.code: {
            const productType = await prodTypeService.get(item.value);
            return productType?.name;
        }
        case POP_UP_AD_DISPLAY_TYPE.FREIGHT_PRICE.code: {
            const shipRoute = await shipRouteService.getInfo(item.value);
            return shipRoute ? `${shipRoute.source}-${shipRoute.destination}` : undefined;
        }
        case POP_UP_AD_DISPLAY_TYPE.AD_PAGE.code: {
            const advert = await advertService.getAdvertDetail(item.value);
            return advert.advert?.title;
        }
        case POP_UP_AD_DISPLAY_TYPE.NEWS.code: {
            const information = await infoService.getInfo(item.value);
            return information.information?.title;
        }
        default:
            return undefined;
    }
}

/** 默认使用 Vue 详情页依赖的业务服务解析跳转名称。 */
const defaultLoadLabelName = (type: number, value: string) => loadPopUpAdvertContentLabel({ type, value });

/** 只补充接口未直接返回的 labelName，保留已有名称并隔离单项解析失败。 */
async function enrichPopUpAdvertContent(
    content: PopUpAdvertContent[],
    loadLabelName: (type: number, value: string) => Promise<string | undefined>
): Promise<PopUpAdvertContent[]> {
    return Promise.all(
        content.map(async item => {
            if (item.labelName || !item.type || !item.value) return item;
            try {
                const labelName = await loadLabelName(Number(item.type), item.value);
                return labelName ? { ...item, labelName } : item;
            } catch {
                return item;
            }
        })
    );
}

/** 按 Vue 详情页契约加载详情，并补齐内容模块的业务名称与缩略图。 */
async function loadPopUpAdvertDetailView(
    id: number,
    load: (id: number, params?: { isSort?: boolean }) => Promise<PopUpAdvertDetail>,
    loadPicture: (id: number) => Promise<{ path?: string }>,
    loadLabelName: (type: number, value: string) => Promise<string | undefined>
): Promise<PopUpAdvertDetail> {
    // 旧 Vue 详情页显式传入 isSort=true，确保内容模块按后台排序顺序返回。
    const result = await load(id, { isSort: true });
    // Vue 详情页在接口未回填缩略图时按 picFileId 补查文件地址，单项失败不影响其它模块。
    const content = await enrichPopUpAdvertContent(result.content || [], loadLabelName);
    const contentWithPictures = await Promise.all(
        content.map(async item => {
            if (item.picFileUrl || !item.picFileId) return item;
            try {
                const picture = await loadPicture(item.picFileId);
                return { ...item, picFileUrl: picture.path };
            } catch {
                return item;
            }
        })
    );
    return { ...result, content: contentWithPictures };
}

/** 弹窗广告详情，保留 Vue 的基础字段、内容模块和状态操作。 */
export function PopUpAdvertDetailPage({
    loadDetail: load = loadPopUpAdvertDetail,
    loadPicture = loadPopUpAdvertPicture,
    loadLabelName = defaultLoadLabelName,
    remove = deletePopUpAdvert,
    updateState = updatePopUpAdvertState,
    permissions
}: PopUpAdvertDetailPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    // 保持 Vue 详情页 v-auth 语义：只有写权限才展示状态和编辑动作。
    const canManage = (permissions ?? storePermissions).includes("ROLE_SS_POPUP_W");
    const banButton = storeBanButton;
    const id = Number(searchParams.get("id"));
    const [detail, setDetail] = useState<PopUpAdvertDetail | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!Number.isFinite(id) || id <= 0) {
            setErrorMessage("弹窗广告编号无效");
            return;
        }
        let disposed = false;
        void loadPopUpAdvertDetailView(id, load, loadPicture, loadLabelName)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("弹窗广告详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, load, loadLabelName, loadPicture]);

    const runAction = async (action: () => Promise<unknown>, failureMessage: string) => {
        try {
            await action();
            if (detail) {
                // Vue 删除/启停成功后都会重新获取详情，避免用本地猜测覆盖服务端字段。
                const refreshedDetail = await loadPopUpAdvertDetailView(detail.id, load, loadPicture, loadLabelName);
                setDetail(refreshedDetail);
            }
            setErrorMessage(null);
        } catch {
            setErrorMessage(failureMessage);
        }
    };

    /** 切换详情状态前二次确认；取消后详情和服务端状态保持不变。 */
    const changeAdvertState = async (id: number, nextState: 2 | 3) => {
        const actionLabel = nextState === 2 ? "启用" : "禁用";
        if (!(await confirmDialog(`是否${actionLabel}该弹窗广告？`))) return;
        await runAction(() => updateState(id, nextState), `弹窗广告${actionLabel}失败`);
    };

    return (
        <section
            aria-label="pop-up-advert-detail-page"
            className="detail-page-transparent flex h-full min-h-full flex-col"
        >
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
            {detail ? (
                <>
                    <div className="section-card mb-8">
                        <ZhDetailHeader
                            leftInfos={[{ label: "编号", value: detail.id }]}
                            rightInfos={[{ label: "创建时间", value: formatDate(detail.createdTime) }]}
                            stateDesc={formatMeta(POP_UP_AD_STATE.getIntroByCode.bind(POP_UP_AD_STATE), detail.state)}
                            title="弹窗广告详情"
                        />
                    </div>
                    <div className="section-card mb-8">
                        {/* 基础字段沿用 Vue detailJson 的展示契约，统一使用 zhihao-ui-react 网格。 */}
                        <ZhGrid cols={4} gap={[12, 12]}>
                            <ZhBaseItem label="弹窗名称" value={detail.name || DEFAULT_VALUE} />
                            <ZhBaseItem label="适用终端" value={formatTerminals(detail.terminal)} />
                            <ZhBaseItem
                                label="显示页面"
                                value={formatMeta(
                                    POP_UP_AD_DISPLAY_PAGE.getIntroByCode.bind(POP_UP_AD_DISPLAY_PAGE),
                                    detail.displayPage
                                )}
                            />
                            <ZhBaseItem
                                label="显示频率"
                                value={formatMeta(
                                    POP_UP_AD_DISPLAY_FREQUENCY.getIntroByCode.bind(POP_UP_AD_DISPLAY_FREQUENCY),
                                    detail.displayFrequency
                                )}
                            />
                        </ZhGrid>
                    </div>
                    <div className="flex flex-col">
                        {(detail.content || []).map((item, index) => (
                            <div
                                className="popup-advert-content-card mb-5 flex w-3/4 shrink-0 items-center rounded-xl border border-solid border-[#d9d9d9] bg-white p-5"
                                key={`${item.picFileId || "content"}-${index}`}
                            >
                                <div className="flex w-full items-center gap-[50px]">
                                    <ZhBaseItem label="缩略图" span={1}>
                                        {item.picFileUrl ? (
                                            <div
                                                data-testid="popup-ad-thumbnail"
                                                style={{
                                                    width: "289px",
                                                    height: "189px",
                                                    background: "#ecf5ff",
                                                    border: "1px solid #cbd8f2",
                                                    borderRadius: "6px"
                                                }}
                                            >
                                                <ZhImage
                                                    alt="弹窗广告缩略图"
                                                    fit="contain"
                                                    height="100%"
                                                    src={item.picFileUrl}
                                                    width="100%"
                                                />
                                            </div>
                                        ) : (
                                            DEFAULT_VALUE
                                        )}
                                    </ZhBaseItem>
                                    <ZhGrid className="flex-1" cols={1} gap={[32, 32]}>
                                        <ZhBaseItem
                                            label="跳转类型"
                                            value={formatMeta(
                                                POP_UP_AD_DISPLAY_TYPE.getIntroByCode.bind(POP_UP_AD_DISPLAY_TYPE),
                                                item.type
                                            )}
                                        />
                                        {Number(item.type) === POP_UP_AD_DISPLAY_TYPE.PRODUCT.code ||
                                        Number(item.type) === POP_UP_AD_DISPLAY_TYPE.SAND_PRICE.code ||
                                        Number(item.type) === POP_UP_AD_DISPLAY_TYPE.FREIGHT_PRICE.code ||
                                        Number(item.type) === POP_UP_AD_DISPLAY_TYPE.AD_PAGE.code ||
                                        Number(item.type) === POP_UP_AD_DISPLAY_TYPE.NEWS.code ? (
                                            <ZhBaseItem
                                                label={
                                                    Number(item.type) === POP_UP_AD_DISPLAY_TYPE.PRODUCT.code
                                                        ? "商品"
                                                        : Number(item.type) === POP_UP_AD_DISPLAY_TYPE.SAND_PRICE.code
                                                          ? "砂石指数"
                                                          : Number(item.type) ===
                                                              POP_UP_AD_DISPLAY_TYPE.FREIGHT_PRICE.code
                                                            ? "运价指数"
                                                            : Number(item.type) === POP_UP_AD_DISPLAY_TYPE.AD_PAGE.code
                                                              ? "广告页"
                                                              : "资讯"
                                                }
                                                value={item.labelName || DEFAULT_VALUE}
                                            />
                                        ) : null}
                                        {Number(item.type) === POP_UP_AD_DISPLAY_TYPE.FUNCTION_PAGE.code ? (
                                            <ZhBaseItem
                                                label="功能页"
                                                value={formatMeta(
                                                    POP_UP_AD_FUNCTION_PAGE.getIntroByCode.bind(
                                                        POP_UP_AD_FUNCTION_PAGE
                                                    ),
                                                    item.value
                                                )}
                                            />
                                        ) : null}
                                        {Number(item.type) === POP_UP_AD_DISPLAY_TYPE.LINK.code ? (
                                            <ZhBaseItem label="链接" value={item.value || DEFAULT_VALUE} />
                                        ) : null}
                                        {detail.terminal?.some(
                                            terminal => Number(terminal) === POP_UP_ADVERT_TERMINAL_TYPE.MOBILE.code
                                        ) ? (
                                            <ZhBaseItem label="排序" value={item.sort ?? DEFAULT_VALUE} />
                                        ) : null}
                                    </ZhGrid>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Space className="operation-fixed-btn" size={0}>
                        {canManage && Number(detail.state) === 2 ? (
                            <Button
                                aria-label={`禁用弹窗广告 ${detail.id}`}
                                disabled={banButton}
                                type="primary"
                                onClick={() => void changeAdvertState(detail.id, 3)}
                            >
                                禁用
                            </Button>
                        ) : canManage ? (
                            <>
                                <Button
                                    aria-label={`编辑弹窗广告 ${detail.id}`}
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => void navigate(`/userOperation/popUpAdvert/edit?id=${detail.id}`)}
                                >
                                    编辑
                                </Button>
                                <Button
                                    aria-label={`启用弹窗广告 ${detail.id}`}
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => void changeAdvertState(detail.id, 2)}
                                >
                                    启用
                                </Button>
                                <Popconfirm
                                    title="确定删除该弹窗广告吗？"
                                    onConfirm={() => void runAction(() => remove(detail.id), "弹窗广告删除失败")}
                                >
                                    <Button
                                        disabled={banButton}
                                        aria-label={`删除弹窗广告 ${detail.id}`}
                                        type="primary"
                                    >
                                        删除
                                    </Button>
                                </Popconfirm>
                            </>
                        ) : null}
                        <Button type="primary" onClick={() => void navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </>
            ) : null}
        </section>
    );
}
